import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { FormikHelpers } from 'formik'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DeleteRemoteConfigModal } from 'src/components/remote-config/delete/DeleteRemoteConfigModal'
import { RemoteConfigFormModal } from 'src/components/remote-config/remote-config-form/RemoteConfigFormModal'
import { RemoteConfigsTable } from 'src/components/remote-config/remote-configs-table/RemoteConfigsTable'
import { RemoteConfigsTableToolbar } from 'src/components/remote-config/remote-configs-table/RemoteConfigsTableToolbar'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import {
  AdminCreateRemoteConfig,
  AdminDeleteRemoteConfig,
  AdminFetchRemoteConfigs,
  AdminUpdateRemoteConfig,
} from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { RemoteConfigApp, remoteConfigAppTitles } from 'src/constants/remote-config/remoteConfig'
import { buildRemoteConfigAppPath } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type {
  DeleteRemoteConfigFormValues,
  RemoteConfig,
  RemoteConfigFormMode,
  RemoteConfigFormValues,
} from 'src/interfaces/remoteConfig'
import {
  getRemoteConfigDeletingId,
  getRemoteConfigSaving,
  getRemoteConfigs,
} from 'src/selectors/remoteConfig'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { filterRemoteConfigs } from 'src/utils/remoteConfig/filterRemoteConfigs'
import {
  describeRemoteConfigDeleteError,
  describeRemoteConfigSaveError,
} from 'src/utils/remoteConfig/remoteConfigErrors'
import { toRemoteConfigValue } from 'src/utils/remoteConfig/remoteConfigValue'

interface FormModalState {
  mode: RemoteConfigFormMode
  remoteConfig: RemoteConfig | null
}

interface Props {
  app: RemoteConfigApp
}

const RemoteConfigKeysPageComponent = ({ app }: Props) => {
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const remoteConfigs = useSelector(getRemoteConfigs)
  const canManage = useHasPermission(InternalPermission.RemoteConfigManage)

  const [searchQuery, setSearchQuery] = useState<string>('')
  const [formModalState, setFormModalState] = useState<FormModalState | null>(null)
  const [remoteConfigPendingDelete, setRemoteConfigPendingDelete] = useState<RemoteConfig | null>(
    null,
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const isSaving = useSelector(getRemoteConfigSaving)
  const deletingId = useSelector(getRemoteConfigDeletingId)
  const isDeletingPendingRow =
    remoteConfigPendingDelete !== null && deletingId === remoteConfigPendingDelete.id

  const visibleRemoteConfigs = useMemo(() => {
    return filterRemoteConfigs(remoteConfigs, app, searchQuery)
  }, [remoteConfigs, app, searchQuery])

  const handleRefresh = useCallback(() => {
    asyncDispatchWithErrorToast(AdminFetchRemoteConfigs.request())
  }, [asyncDispatchWithErrorToast])

  const handleOpenCreate = useCallback(() => {
    setFormModalState({ mode: 'create', remoteConfig: null })
  }, [])

  // A view-only user gets the same form read-only (3.4).
  const handleOpenRemoteConfig = useCallback(
    (remoteConfig: RemoteConfig) => {
      setFormModalState({ mode: canManage ? 'edit' : 'view', remoteConfig })
    },
    [canManage],
  )

  const handleOpenEdit = useCallback((remoteConfig: RemoteConfig) => {
    setFormModalState({ mode: 'edit', remoteConfig })
  }, [])

  const handleOpenDelete = useCallback((remoteConfig: RemoteConfig) => {
    setRemoteConfigPendingDelete(remoteConfig)
  }, [])

  // Guarded so no dismissal path can close a modal whose request is still running.
  const handleCloseFormModal = useCallback(() => {
    if (isSaving) {
      return
    }

    setFormModalState(null)
  }, [isSaving])

  const handleCloseDeleteModal = useCallback(() => {
    if (isDeletingPendingRow) {
      return
    }

    setRemoteConfigPendingDelete(null)
  }, [isDeletingPendingRow])

  // Someone else deleted the key: the open modal can't act on it any more.
  const handleKeyGone = useCallback(
    (message: string) => {
      snackbar?.enqueueSnackbar(message, { variant: 'error' })
      setFormModalState(null)
      setRemoteConfigPendingDelete(null)
      handleRefresh()
    },
    [snackbar, handleRefresh],
  )

  const handleSubmit = useCallback(
    async (values: RemoteConfigFormValues, helpers: FormikHelpers<RemoteConfigFormValues>) => {
      if (!formModalState) {
        return
      }

      helpers.setStatus(undefined)

      try {
        if (formModalState.mode === 'edit' && formModalState.remoteConfig) {
          await createAsyncAction(
            dispatch,
            AdminUpdateRemoteConfig.request({
              remoteConfigId: formModalState.remoteConfig.id,
              value: toRemoteConfigValue(values),
              enabled: values.enabled,
              description: values.description.trim(),
            }),
          )
          snackbar?.enqueueSnackbar('Key saved', { variant: 'success' })
          setFormModalState(null)

          return
        }

        await createAsyncAction(
          dispatch,
          AdminCreateRemoteConfig.request({
            key: values.key.trim(),
            app: values.app,
            type: values.type,
            value: toRemoteConfigValue(values),
            enabled: values.enabled,
            description: values.description.trim(),
          }),
        )
        snackbar?.enqueueSnackbar('Key created', { variant: 'success' })
        setFormModalState(null)

        // The new key shows in its own app's list.
        if (values.app !== app) {
          navigate(buildRemoteConfigAppPath(values.app))
        }
      } catch (errorCode: unknown) {
        const saveError = describeRemoteConfigSaveError(errorCode, values)

        if (saveError.target === 'gone') {
          handleKeyGone(saveError.message)

          return
        }

        if (saveError.target === 'form') {
          helpers.setStatus({ formError: saveError.message })

          return
        }

        helpers.setFieldError(saveError.target, saveError.message)
      }
    },
    [formModalState, dispatch, snackbar, app, navigate, handleKeyGone],
  )

  const handleConfirmDelete = useCallback(
    async (helpers: FormikHelpers<DeleteRemoteConfigFormValues>) => {
      if (!remoteConfigPendingDelete) {
        return
      }

      helpers.setStatus(undefined)

      try {
        await createAsyncAction(
          dispatch,
          AdminDeleteRemoteConfig.request({ remoteConfigId: remoteConfigPendingDelete.id }),
        )
        snackbar?.enqueueSnackbar(`${remoteConfigPendingDelete.key} deleted`, {
          variant: 'success',
        })
        setRemoteConfigPendingDelete(null)
      } catch (errorCode: unknown) {
        const deleteError = describeRemoteConfigDeleteError(errorCode)

        if (deleteError.target === 'gone') {
          handleKeyGone(deleteError.message)

          return
        }

        helpers.setStatus({ formError: deleteError.message })
      }
    },
    [remoteConfigPendingDelete, dispatch, snackbar, handleKeyGone],
  )

  return (
    <Fragment>
      <div
        data-testid="remote-config-keys-page"
        css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-6`}
      >
        <div css={tw`mb-5 flex flex-row items-center justify-between gap-4`}>
          <Typography variant="h2">{remoteConfigAppTitles[app]} keys</Typography>
          {canManage ? (
            <Button
              size="small"
              color="dark"
              className={tw`px-4`}
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenCreate}
              data-testid="remote-config-new-key-button"
            >
              New key
            </Button>
          ) : null}
        </div>
        <RemoteConfigsTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
        <RemoteConfigsTable
          remoteConfigs={visibleRemoteConfigs}
          searchQuery={searchQuery}
          canManage={canManage}
          onOpenRemoteConfig={handleOpenRemoteConfig}
          onEditRemoteConfig={handleOpenEdit}
          onDeleteRemoteConfig={handleOpenDelete}
        />
      </div>
      <RemoteConfigFormModal
        isOpen={Boolean(formModalState)}
        mode={formModalState?.mode ?? 'create'}
        remoteConfig={formModalState?.remoteConfig ?? null}
        defaultApp={app}
        existingRemoteConfigs={remoteConfigs}
        isSaving={isSaving}
        onSubmit={handleSubmit}
        onClose={handleCloseFormModal}
      />
      <DeleteRemoteConfigModal
        isOpen={Boolean(remoteConfigPendingDelete)}
        remoteConfig={remoteConfigPendingDelete}
        isDeleting={isDeletingPendingRow}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteModal}
      />
    </Fragment>
  )
}

export const RemoteConfigKeysPage = memo(RemoteConfigKeysPageComponent)
